/**
 * Arrears ageing — who owes me, and for how long?
 *
 * **Age is the whole point.** A debt at ninety days is a different problem from one at thirty: the
 * first needs a solicitor and the second needs a telephone call, and a report that gives one total
 * cannot tell them apart. So every figure here is bucketed, and the buckets are `AGEING_BUCKETS`
 * in core — the same five the dashboard, the reminder ladder and the tenant's own statement use.
 *
 * The scale that draws them steps **down in lightness** as the debt gets older, so the order
 * survives greyscale, a monochrome printer and a reader who sees no colour at all. `Current` sits
 * outside that ramp, in neutral, because it is not arrears — it is money that is not yet due, and
 * colouring it as the mildest kind of late would say something untrue about it.
 *
 * The by-building half is here because a single block quietly carrying half an agency's arrears is
 * a fact about the building, not about five separate tenants — and it is the view that decides
 * whether somebody drives out there.
 */

import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { AGEING_BUCKETS, type AgeingBucket } from "@rentorng/core";
import { api, downloadCsv } from "@/lib/api";
import { useFormat } from "@/lib/format";
import { useSession } from "@/lib/session";
import { AGEING_SCALE, ColumnChart, CompositionBar, SegmentKey } from "@/components/charts";
import { Button, Card, CardTitle, EmptyState, Table } from "@/components/ui";
import { ChartCard, Figure, FigureRow, ReportHeader, ReportState, useCompactMoney } from "./shared";

interface Arrears {
  rows: Array<{
    key: string;
    label: string;
    total: number;
    buckets: Record<AgeingBucket, number>;
    contact: { phone: string | null; email: string | null } | null;
  }>;
  totals: Record<AgeingBucket, number>;
  grandTotal: number;
  overdue: number;
  byProperty: Array<{
    propertyId: string | null;
    propertyName: string;
    total: number;
    buckets: Record<AgeingBucket, number>;
  }>;
  asOf: string;
}

/**
 * The texture each band carries **on the column chart**, where every band has a bar whether or not
 * it has anything in it.
 *
 * The composition bar does not use this and must not: it draws only the bands that have money in
 * them, so a texture pinned to a band name vanishes on the day that band is empty. It decides its
 * own, by drawn position — `alternatingTextures` in the chart kit says why.
 */
const TEXTURES: Record<AgeingBucket, "solid" | "hatch" | "grid"> = {
  current: "solid",
  d1_30: "solid",
  d31_60: "hatch",
  d61_90: "solid",
  d90_plus: "grid",
};

export function ArrearsReportPage() {
  const fmt = useFormat();
  const compact = useCompactMoney();
  const { vocab } = useSession();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["report-arrears"],
    queryFn: () => api.get<Arrears>("/reports/arrears"),
  });

  if (!data) {
    return (
      <div>
        <ReportHeader title="Arrears ageing" question="Who owes me, and for how long has it been outstanding?" />
        <ReportState isLoading={isLoading} error={error} retry={() => refetch()} />
      </div>
    );
  }

  const full = (value: number) => fmt.money(value);

  const segments = AGEING_BUCKETS.map((bucket) => ({
    label: bucket.label,
    value: data.totals[bucket.key],
    colour: AGEING_SCALE[bucket.key],
  }));

  /* The four buildings carrying the most, and never more than four on the chart: past that, the
     bars are too thin to compare and the table underneath is the better instrument. */
  const worstBuildings = data.byProperty.slice(0, 6);

  return (
    <div>
      <ReportHeader
        title="Arrears ageing"
        question="Who owes me, how much, and for how long has it been outstanding?"
        subtitle={`${compact(data.overdue)} past due · as at ${fmt.date(data.asOf)}`}
        actions={
          <Button variant="secondary" onClick={() => downloadCsv("arrears")}>
            Export CSV
          </Button>
        }
      />

      {data.rows.length === 0 ? (
        <Card>
          <EmptyState title="Nobody is in arrears." hint="That is the product working." />
        </Card>
      ) : (
        <div className="space-y-5">
          <FigureRow>
            <Figure label="Outstanding" value={compact(data.grandTotal)} sub="Everything unpaid, due or not" />
            <Figure label="Past due" value={compact(data.overdue)} tone="bad" sub="Past the grace period" />
            <Figure
              label="Older than 90 days"
              value={compact(data.totals.d90_plus)}
              tone={data.totals.d90_plus > 0 ? "bad" : "good"}
              sub="Rarely arrives without a conversation"
            />
            <Figure label="Debtors" value={data.rows.length} sub={`Owing something today`} />
          </FigureRow>

          <ChartCard
            title="How old the debt is"
            hint="Current is not arrears — it is money not yet due. The four bands after it get darker as they get older, so the order reads with the colour removed."
          >
            <CompositionBar label="Arrears by age" segments={segments} format={full} />
            <SegmentKey segments={segments} format={full} />
          </ChartCard>

          <ChartCard
            title={`Where the arrears are`}
            hint={`The same money grouped by ${vocab.property} rather than by debtor. One block carrying most of it is a fact about the block.`}
          >
            {worstBuildings.length === 0 ? (
              <EmptyState title="Nothing outstanding against a building." />
            ) : (
              <ColumnChart
                label="Arrears by building, split by age"
                mode="stacked"
                height={230}
                highlight={-1}
                points={worstBuildings.map((row) => ({
                  label: row.propertyName.length > 12 ? `${row.propertyName.slice(0, 11)}…` : row.propertyName,
                  full: row.propertyName,
                  values: AGEING_BUCKETS.map((bucket) => row.buckets[bucket.key]),
                }))}
                series={AGEING_BUCKETS.map((bucket) => ({
                  label: bucket.label,
                  colour: AGEING_SCALE[bucket.key],
                  texture: TEXTURES[bucket.key],
                }))}
                format={compact}
                footnote={
                  data.byProperty.length > worstBuildings.length
                    ? `The ${worstBuildings.length} carrying the most, of ${data.byProperty.length}. The rest are in the figures.`
                    : undefined
                }
              />
            )}
          </ChartCard>

          <Card padded={false}>
            <div className="p-5 sm:p-6">
              <CardTitle hint="Biggest debt first. Somebody with no email address cannot be chased automatically, and is marked.">
                Who owes it
              </CardTitle>
            </div>
            <Table
              head={
                <tr>
                  <th>Client</th>
                  {AGEING_BUCKETS.map((bucket) => (
                    <th key={bucket.key} className="num">
                      {bucket.label}
                    </th>
                  ))}
                  <th className="num">Total</th>
                  <th></th>
                </tr>
              }
            >
              {data.rows.map((row) => (
                <tr key={row.key}>
                  <td>
                    <Link to={`/app/clients/${row.key}`} className="link">
                      {row.label}
                    </Link>
                    {row.contact?.email ? null : <span className="block text-xs text-warn-700">no email</span>}
                  </td>
                  {AGEING_BUCKETS.map((bucket) => (
                    <td key={bucket.key} className="num tabular">
                      {row.buckets[bucket.key] > 0 ? full(row.buckets[bucket.key]) : <span className="text-ink-300">—</span>}
                    </td>
                  ))}
                  <td className="num tabular font-medium text-danger-700">{full(row.total)}</td>
                  <td>
                    <Link to={`/app/reminders/send?partyId=${row.key}`} className="link text-xs">
                      Chase
                    </Link>
                  </td>
                </tr>
              ))}
              <tr className="font-medium">
                <td>Total</td>
                {AGEING_BUCKETS.map((bucket) => (
                  <td key={bucket.key} className="num tabular">
                    {full(data.totals[bucket.key])}
                  </td>
                ))}
                <td className="num tabular">{full(data.grandTotal)}</td>
                <td></td>
              </tr>
            </Table>
          </Card>

          <Card padded={false}>
            <div className="p-5 sm:p-6">
              <CardTitle>By {vocab.property}</CardTitle>
            </div>
            <Table
              head={
                <tr>
                  <th>{vocab.property}</th>
                  {AGEING_BUCKETS.map((bucket) => (
                    <th key={bucket.key} className="num">
                      {bucket.label}
                    </th>
                  ))}
                  <th className="num">Total</th>
                </tr>
              }
            >
              {data.byProperty.map((row) => (
                <tr key={row.propertyId ?? "unattached"}>
                  <td>
                    {row.propertyId ? (
                      <Link to={`/app/properties/${row.propertyId}`} className="link">
                        {row.propertyName}
                      </Link>
                    ) : (
                      <span className="text-ink-500">{row.propertyName}</span>
                    )}
                  </td>
                  {AGEING_BUCKETS.map((bucket) => (
                    <td key={bucket.key} className="num tabular">
                      {row.buckets[bucket.key] > 0 ? full(row.buckets[bucket.key]) : <span className="text-ink-300">—</span>}
                    </td>
                  ))}
                  <td className="num tabular font-medium">{full(row.total)}</td>
                </tr>
              ))}
            </Table>
          </Card>
        </div>
      )}
    </div>
  );
}
